import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useEffectWithSingleDependency } from './useEffectWithSingleDependency';
import { TalentConversationLocation } from 'lib/enum/lead-conversation-location';

type FilterTemplate = Record<string, string | null | boolean | number | string[]>;

function useUrlSearchParams<FilterMap = FilterTemplate>(props?: {
    initialFilters?: Partial<FilterMap>;
}): {
    setFilter: (filter: Partial<FilterMap>) => void;
    currentLocation: TalentConversationLocation | null;
    unsetFilter: (filterName: keyof FilterMap) => void;
    unsetMultipleFilters: (filterNames: (keyof FilterMap)[]) => void;
} {
    const navigate = useNavigate();
    const location = useLocation();

    const searchParams = new URLSearchParams(location.search);

    let currentLocation = null;

    if (searchParams.get('location') === TalentConversationLocation.Inbox) {
        currentLocation = TalentConversationLocation.Inbox;
    } else if (searchParams.get('location') === TalentConversationLocation.Archive) {
        currentLocation = TalentConversationLocation.Archive;
    } else if (searchParams.get('location') === TalentConversationLocation.Spam) {
        currentLocation = TalentConversationLocation.Spam;
    }

    const applyFilters = useCallback(
        (newFilters: [keyof FilterMap, FilterMap[keyof FilterMap] | void][]): void => {
            const newHistoryLocationSearch = applyFiltersHelper(newFilters, location.search);
            const locationSearchChanged = newHistoryLocationSearch !== window.location.search;

            if (locationSearchChanged || location.hash) {
                navigate({
                    search: newHistoryLocationSearch,
                    hash: location.hash,
                });
            }
        },
        [location.hash, location.search, navigate],
    );

    const setFilter = useCallback(
        (filter: Partial<FilterMap>, asEmpty = false): void => {
            const filterObjectToFilterMap = setFilterHelper(filter, asEmpty);
            applyFilters(filterObjectToFilterMap);
        },
        [applyFilters],
    );

    const unsetFilter = useCallback(
        (filterName: keyof FilterMap): void => {
            applyFilters([[filterName, undefined as void]]);
        },
        [applyFilters],
    );

    const unsetMultipleFilters = useCallback(
        (filterNames: (keyof FilterMap)[]): void => {
            const filtersToUnset: [keyof FilterMap, FilterMap[keyof FilterMap] | void][] = filterNames.map(
                filterName => [filterName, undefined as void],
            );
            applyFilters(filtersToUnset);
        },
        [applyFilters],
    );

    useEffectWithSingleDependency(() => {
        if (props?.initialFilters instanceof Object) {
            const filtersMap = Object.entries(props.initialFilters).map(
                ([name, value]) => [name, value] as [keyof FilterMap, void | FilterMap[keyof FilterMap]],
            );
            applyFilters(filtersMap);
        }
    }, props?.initialFilters);

    return {
        setFilter,
        currentLocation,
        unsetFilter,
        unsetMultipleFilters,
    };
}

function applyFiltersHelper<FilterMap = FilterTemplate>(
    newFilters: [keyof FilterMap, FilterMap[keyof FilterMap] | void][],
    historyLocationSearch: string,
): string | undefined {
    const urlParams = new URLSearchParams(historyLocationSearch);

    for (const [key, value] of newFilters) {
        const paramKey = String(key);

        urlParams.delete(paramKey);

        if (
            value === null ||
            value === undefined ||
            (Array.isArray(value) && value.length === 0) ||
            (typeof value === 'string' && value.trim() === '')
        ) {
            continue;
        }

        if (Array.isArray(value)) {
            for (const v of value) {
                urlParams.append(paramKey, String(v));
            }
        } else {
            urlParams.set(paramKey, String(value));
        }
    }

    const orderedParams = new URLSearchParams();
    for (const [key] of newFilters) {
        const paramKey = String(key);
        const values = urlParams.getAll(paramKey);

        if (values.length > 0) {
            for (const v of values) {
                orderedParams.append(paramKey, v);
            }
        }
    }

    for (const [key, value] of urlParams.entries()) {
        if (!newFilters.some(([k]) => String(k) === key)) {
            orderedParams.append(key, value);
        }
    }

    const query = orderedParams.toString();

    return query ? `?${query}` : undefined;
}

function setFilterHelper<FilterMap = FilterTemplate>(
    filter: Partial<FilterMap>,
    asEmpty = false,
): [keyof FilterMap, FilterMap[keyof FilterMap]][] {
    const filterObjectToFilterMap: [keyof FilterMap, FilterMap[keyof FilterMap]][] = Object.entries(filter).map(
        e => [e[0], asEmpty ? '' : e[1]] as [keyof FilterMap, FilterMap[keyof FilterMap]],
    );

    return filterObjectToFilterMap;
}

useUrlSearchParams.setFilterHelper = setFilterHelper;
useUrlSearchParams.applyFiltersHelper = applyFiltersHelper;

export { useUrlSearchParams };

export type { FilterTemplate };
